import { PaymentError, InitializePaymentInterface } from "../types";
import { PaymentInterface } from "./paymentInterface";
import { create, Dropin, paypalCreateOptions } from "braintree-web-drop-in";
import { HtmlIdentifier } from "shared/utils/constants";
import { PayPalFlowType } from "shared/utils/enums";

export class BraintreePayment implements PaymentInterface {
  initToken: string;
  isPaypalEnabled: boolean;
  deleteCardAllowed: boolean;
  isThreeDSecureEnabled: boolean;
  amount: number;
  currencyCode: string;
  setDisplayLoader?: Function;
  setPayButtonDisabled: Function;
  setPaymentError: Function;
  setPaymentNonce: Function;
  webDropInInstance: Dropin | undefined;

  constructor(paymentInitialize: InitializePaymentInterface) {
    this.initToken = paymentInitialize.apiPaymentInitToken.initToken;
    this.isPaypalEnabled = paymentInitialize.apiPaymentInitToken.isPaypalEnabled;
    this.deleteCardAllowed = paymentInitialize.apiPaymentInitToken.deleteCardAllowed;
    this.isThreeDSecureEnabled = paymentInitialize.apiPaymentInitToken.threeDSecureEnabled;
    this.amount = paymentInitialize.amountObj.value;
    this.currencyCode = paymentInitialize.amountObj.currencyCode;
    this.setDisplayLoader = paymentInitialize.setDisplayLoader;
    this.setPayButtonDisabled = paymentInitialize.setPayButtonDisabled;
    this.setPaymentError = paymentInitialize.setPaymentError;
    this.setPaymentNonce = paymentInitialize.setPaymentNonce;
  }

  updateInitToken(token: string): void {
    this.initToken = token;
  }

  initialize(): void {
    this.setPayButtonDisabled(true);
    this.setDisplayLoader?.(true);
    const braintreeConfig = {
      container: `#${HtmlIdentifier.PAYMENT_CONTAINER}`,
      authorization: this.initToken,
      vaultManager: this.deleteCardAllowed,
      threeDSecure: Boolean(this.isThreeDSecureEnabled),
      // 3d secure cards are failing as by default vaultCard option is true
      // hence, we don't want to save the card in vault
      // by default the vaultCard is true
      card: {
        vault: {
          vaultCard: false
        }
      }
    };

    let paypalObj: paypalCreateOptions | undefined;
    // if PayPal is enabled then add PayPal specific configuration
    if (this.isPaypalEnabled) {
      paypalObj = {
        flow: PayPalFlowType.CHECKOUT,
        amount: this.amount,
        currency: this.currencyCode
      };
    }

    // initializes the braintree web drop-in and also renders the UI
    create({
      ...braintreeConfig,
      paypal: paypalObj
    })
      .then(webDropInInstance => {
        // we will need this instance later to destroy the drop-in instance when retrying for payment
        // if we don't destroy then we get same nonce(token for completing payment) everytime
        this.webDropInInstance = webDropInInstance;
        // Pay/Purchase button should be enabled since braintree web drop-in created successfully
        this.setPayButtonDisabled(false);
      })
      .catch(dropInError => {
        // some error occurred due to which braintree drop-in could not created
        // Pay/Purchase button will be in disabled state
        // todo:rr following value should be sent using i18
        this.setPaymentError(PaymentError.COULD_NOT_INITIALIZE);
      })
      .finally(() => {
        this.setDisplayLoader?.(false);
      });
  }

  getTokenForPayment(): void {
    // once a user clicks on Pay/Purchase button disable it
    //    else they might click it twice
    this.setPayButtonDisabled(true);
    // todo:rr when integrated in reg check if we want to keep it else remove loader logic
    //    test if any flicker in loader if we want to keep it
    this.setDisplayLoader?.(true);
    // 3DSecure options
    const threeDSecure = { amount: this.amount ? `${this.amount}` : "", challengeRequested: true };
    // get the nonce for selected payment method
    this.webDropInInstance
      ?.requestPaymentMethod({ threeDSecure })
      .then(paymentMethodPayload => {
        if (!paymentMethodPayload) {
          // this is added as a safety net
          this.setPaymentError(PaymentError.COULD_NOT_GET_NONCE);
        }
        // set the nonce to be used in api for payment
        this.setPaymentNonce(paymentMethodPayload.nonce);
      })
      .catch(() => {
        this.setPaymentError(PaymentError.COULD_NOT_GET_NONCE);
      })
      .finally(() => {
        this.setPayButtonDisabled(false);
        this.setDisplayLoader?.(false);
      });
  }

  retryGetTokenForPayment(): void {
    this.setPayButtonDisabled(true);
    this.setDisplayLoader?.(true);
    // todo:rr check after integrated in reg
    // destroy the drop-in and reinitialize/recreate drop-in on successful teardown
    //    else gives same Nonce
    this.webDropInInstance
      ?.teardown()
      .then(() => {
        this.initialize();
      })
      .catch(() => {
        this.setDisplayLoader?.(false);
        this.setPaymentError(PaymentError.COULD_NOT_GET_NONCE);
      });
    // Pay/Purchase button will remain disabled
    // todo:rr test the scenario after reg integration
  }
}
